import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  getDefaultOgImageUrl,
  getPublicSiteUrl,
  toAbsolutePublicUrl,
} from '../../pages/new/siteUrl';
import { JsonLdScript } from './JsonLdScript';
import {
  buildArticleJsonLd,
  buildBookJsonLd,
  buildBreadcrumbJsonLd,
  buildChapterJsonLd,
  buildDefinedTermJsonLd,
  buildFaqPageJsonLd,
  buildOrganizationJsonLd,
  buildWebSiteJsonLd,
} from './jsonLd';
import { usePrerenderReady } from './usePrerenderReady';

const DEFAULT_TITLE = 'Charlee.ai';
const DEFAULT_DESCRIPTION = 'Charlee.ai — AI for insurance claims and underwriting.';

function trimNonEmpty(value) {
  if (value == null || typeof value !== 'string') return '';
  const t = value.trim();
  return t || '';
}

function normalizePath(path) {
  const p = trimNonEmpty(path);
  if (!p) return '';
  return p.startsWith('/') ? p : `/${p}`;
}

function articleHeadlineFromTitle(pageTitle) {
  const stripped = pageTitle.replace(/\s*\|\s*Charlee\.ai(?:\s.*)?$/i, '').trim();
  return stripped || pageTitle;
}

function isValidDateString(value) {
  const s = trimNonEmpty(value);
  if (!s) return false;
  const t = new Date(s).getTime();
  return !Number.isNaN(t);
}

function toSiteAbsoluteUrl(siteUrl, pathOrUrl) {
  const value = trimNonEmpty(pathOrUrl);
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value)) return value;
  const path = normalizePath(value);
  if (!siteUrl || !path) return undefined;
  return `${siteUrl}${path}`;
}

function resolveBreadcrumbCrumbs(breadcrumbItems, siteUrl) {
  if (!Array.isArray(breadcrumbItems) || !siteUrl) return [];
  return breadcrumbItems
    .map((item) => {
      const name = trimNonEmpty(item?.name || item?.label);
      if (!name) return null;
      const explicitUrl = trimNonEmpty(item?.url);
      const path = normalizePath(item?.path);
      const url = explicitUrl || (path ? `${siteUrl}${path}` : '');
      if (!url) return null;
      return { name, url };
    })
    .filter(Boolean);
}

/**
 * SEO helmet for static marketing/product and Knowledge Center pages.
 * Define pageTitle, metaDescription, canonicalPath (and optional fields) in each page file.
 */
export function PageSeoHelmet({
  pageTitle,
  metaDescription,
  canonicalPath,
  canonicalUrl: canonicalUrlProp,
  ogImage,
  ogType = 'website',
  noIndex = false,
  siteJsonLd = false,
  keywords,
  articlePublishedTime,
  articleModifiedTime,
  author,
  authorType = 'Person',
  breadcrumbItems,
  faqItems,
  definedTerm,
  book,
  chapter,
  headline,
}) {
  const title = trimNonEmpty(pageTitle) || DEFAULT_TITLE;
  const description = trimNonEmpty(metaDescription) || DEFAULT_DESCRIPTION;
  const image =
    toAbsolutePublicUrl(trimNonEmpty(ogImage)) || getDefaultOgImageUrl();
  const ogTypeSafe = ogType === 'article' ? 'article' : 'website';
  const keywordsSafe = trimNonEmpty(keywords);
  const showArticleTime = isValidDateString(articlePublishedTime);
  const articleTimeContent = showArticleTime
    ? new Date(trimNonEmpty(articlePublishedTime)).toISOString()
    : '';
  const showModifiedTime = isValidDateString(articleModifiedTime);
  const modifiedTimeContent = showModifiedTime
    ? new Date(trimNonEmpty(articleModifiedTime)).toISOString()
    : '';
  const authorName = trimNonEmpty(author);
  const articleHeadline =
    trimNonEmpty(headline) || articleHeadlineFromTitle(title);

  const canonicalUrl = useMemo(() => {
    const explicit = trimNonEmpty(canonicalUrlProp);
    if (explicit) return explicit;
    const siteUrl = getPublicSiteUrl();
    const path = normalizePath(canonicalPath);
    if (!siteUrl || !path) return '';
    return `${siteUrl}${path}`;
  }, [canonicalUrlProp, canonicalPath]);

  const siteUrl = getPublicSiteUrl();

  const jsonLdData = useMemo(() => {
    const graph = [];

    if (siteJsonLd && siteUrl) {
      const logoUrl = getDefaultOgImageUrl();
      graph.push(buildOrganizationJsonLd({ siteUrl, logoUrl }));
      graph.push(buildWebSiteJsonLd({ siteUrl }));
    }

    const chapterBookUrl = toSiteAbsoluteUrl(siteUrl, chapter?.bookUrl);
    const definedTermSetUrl = toSiteAbsoluteUrl(
      siteUrl,
      definedTerm?.termSetUrl
    );

    if (book?.name && canonicalUrl) {
      graph.push(
        buildBookJsonLd({
          name: book.name,
          description,
          url: canonicalUrl,
          image: image || undefined,
          dateModified: modifiedTimeContent || undefined,
          numberOfPages: book.numberOfPages,
          author: authorName || 'Charlee.ai',
        })
      );
    } else if (chapter?.name && canonicalUrl) {
      graph.push(
        buildChapterJsonLd({
          name: chapter.name,
          description,
          url: canonicalUrl,
          image: image || undefined,
          dateModified: modifiedTimeContent || undefined,
          position: chapter.position,
          bookName: chapter.bookName,
          bookUrl: chapterBookUrl,
          author: authorName || 'Charlee.ai',
        })
      );
    } else if (ogTypeSafe === 'article' && canonicalUrl) {
      graph.push(
        buildArticleJsonLd({
          headline: articleHeadline,
          description,
          url: canonicalUrl,
          image: image || undefined,
          datePublished: showArticleTime ? articleTimeContent : undefined,
          dateModified: modifiedTimeContent || undefined,
          author: authorName || undefined,
          authorType,
          publisher: 'Charlee.ai',
        })
      );
    }

    if (definedTerm?.name && canonicalUrl) {
      graph.push(
        buildDefinedTermJsonLd({
          name: definedTerm.name,
          description: definedTerm.description || description,
          url: canonicalUrl,
          termSetName: definedTerm.termSetName,
          termSetUrl: definedTermSetUrl,
        })
      );
    }

    if (Array.isArray(faqItems) && faqItems.length > 0 && canonicalUrl) {
      graph.push(
        buildFaqPageJsonLd({
          url: canonicalUrl,
          questions: faqItems,
        })
      );
    }

    const crumbs = resolveBreadcrumbCrumbs(breadcrumbItems, siteUrl);
    if (crumbs.length > 0) {
      graph.push(buildBreadcrumbJsonLd(crumbs));
    }

    return graph;
  }, [
    siteJsonLd,
    siteUrl,
    book,
    chapter,
    canonicalUrl,
    description,
    image,
    modifiedTimeContent,
    authorName,
    ogTypeSafe,
    articleHeadline,
    showArticleTime,
    articleTimeContent,
    authorType,
    definedTerm,
    faqItems,
    breadcrumbItems,
  ]);

  usePrerenderReady(true);

  return (
    <>
      {jsonLdData.length > 0 ? <JsonLdScript data={jsonLdData} /> : null}
      <Helmet prioritizeSeoTags>
        <title>{title}</title>
        <meta name="description" content={description} />
        {keywordsSafe ? <meta name="keywords" content={keywordsSafe} /> : null}
        {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
        <meta property="og:type" content={ogTypeSafe} />
        {image ? <meta property="og:image" content={image} /> : null}
        <meta
          name="twitter:card"
          content={image ? 'summary_large_image' : 'summary'}
        />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {image ? <meta name="twitter:image" content={image} /> : null}
        {showArticleTime && articleTimeContent ? (
          <meta property="article:published_time" content={articleTimeContent} />
        ) : null}
        {showModifiedTime && modifiedTimeContent ? (
          <meta property="article:modified_time" content={modifiedTimeContent} />
        ) : null}
        {authorName ? <meta name="author" content={authorName} /> : null}
        {noIndex ? <meta name="robots" content="noindex" /> : null}
      </Helmet>
    </>
  );
}
